import {
  Badge,
  Box,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react'
import { ColumnDef } from '@tanstack/react-table'
import { Check, RotateCw, Search } from 'lucide-react'
import { useState } from 'react'

import {
  Dialog,
  DialogDrawer,
  DialogTrigger,
} from '@/components/shared/bundles/Drawer'
import { Table } from '@/components/shared/displayers/Table'
import { useAuthStore } from '@/features/sessao/store/useAuthStore'
import { useGetUserList } from '@/features/usuarios/hooks/get-user-list'
import { useDebounce_new } from '@/hooks/useDebounce'
import { User, user_role } from '@/services/backend/config/entities'

type SafeUser = Omit<User, 'password'>

type Props = {
  role?: user_role
  onSelect: (data: SafeUser) => void
}

function Form({ role, onSelect }: Props) {
  const { user } = useAuthStore()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce_new(search)

  const {
    query: { data, isFetching, refetch },
  } = useGetUserList(debouncedSearch, role)

  const columns: ColumnDef<SafeUser>[] = [
    {
      header: 'Nome',
      accessorKey: 'name',
      sortingFn: (a, b) =>
        a.original.displayName > b.original.displayName ? 1 : -1,
      cell: ({ row }) => {
        const isMe = row.original.id === user?.id

        return (
          <Flex gap={2}>
            <Box as="span" fontWeight={isMe ? 'bold' : 'normal'}>
              {row.original.displayName}
            </Box>
            {isMe && (
              <Badge colorScheme="primary" variant="subtle">
                Eu
              </Badge>
            )}
          </Flex>
        )
      },
    },
    {
      header: '',
      accessorKey: 'id',
      enableSorting: false,
      cell: ({ row }) => (
        <HStack justifyContent="flex-end">
          <IconButton
            colorScheme="primary"
            aria-label="Selecionar"
            icon={<Icon as={Check} />}
            onClick={() => onSelect(row.original)}
          />
        </HStack>
      ),
    },
  ]

  return (
    <>
      <InputGroup mb={4}>
        <InputLeftElement>
          <Icon as={Search} />
        </InputLeftElement>

        <Input
          placeholder="Pesquisar"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <InputRightElement>
          {isFetching ? (
            <Spinner />
          ) : (
            <IconButton
              aria-label="Pesquisar"
              icon={<Icon as={RotateCw} />}
              onClick={() => refetch()}
              variant="ghost"
            />
          )}
        </InputRightElement>
      </InputGroup>

      <Table
        data={data ?? []}
        columns={columns}
        initialSorting={[{ id: 'name', desc: false }]}
      />
    </>
  )
}

// export default function Component({ onSelect, ...props }: Props)  {
//   const { isOpen, onOpen, onClose } = useDisclosure();

//   return (
//     <>
//       <IconButton aria-label="Pesquisar" icon={<Icon as={Search} />} onClick={onOpen} colorScheme="primary" />

//       <Modal isOpen={isOpen} onClose={onClose}>
//         <ModalOverlay />
//         <ModalContent>
//           <ModalHeader>Pesquisar</ModalHeader>
//           <ModalCloseButton />
//           <ModalBody>
//             <Form
//               onSelect={(res) => {
//                 onSelect(res);
//                 onClose();
//               }}
//               {...props}
//             />
//           </ModalBody>
//         </ModalContent>
//       </Modal>
//     </>
//   );
// };

export default function Component({ onSelect }: Props) {
  const { isOpen, onOpen, onClose } = useDisclosure()

  return (
    <Dialog handler={{ isOpen, onOpen, onClose }}>
      <DialogTrigger>
        <IconButton
          aria-label="Pesquisar"
          icon={<Icon as={Search} />}
          onClick={onOpen}
          colorScheme="primary"
        />
      </DialogTrigger>

      <DialogDrawer>
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Usuários</DrawerHeader>
          <DrawerBody>
            <Form
              onSelect={(res) => {
                onSelect(res)
                onClose()
              }}
            />
          </DrawerBody>
        </DrawerContent>
      </DialogDrawer>
    </Dialog>
  )
}
